import React, { useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router-dom";
import { isNil, map, prop } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import { RoleConfirmation, roleDetailsValidationSchema, RoleForm } from "components/views/roleEdit";
import type { RoleDetails, RoleFormData } from "components/views/roleEdit/types";
import { UpdateRole, useGetPermissionGroupsQuery, useGetRoleDetailsQuery } from "gql";

const roleToFormData = (role?: RoleDetails): Maybe<RoleFormData> => {
  if (!isNil(role)) {
    const { permissionGroups, ...roleData } = role;
    return {
      ...roleData,
      permissionGroups: map(prop("id"), permissionGroups),
    };
  }
  return null;
};

export const EditRolePage: React.FC = () => {
  const { id } = useParams();
  const { showBoundary: onError } = useErrorBoundary();

  const { data: roleData, loading: roleLoading } = useGetRoleDetailsQuery({
    onError,
    variables: { roleId: id as string },
  });
  const role = roleData?.role;

  const { data: permissionGroupsData, loading: permissionsLoading } = useGetPermissionGroupsQuery({ onError });
  const permissionGroups = permissionGroupsData?.permissionGroups;

  const validateRole = useDryRunMutation(UpdateRole, {
    varSetter: (role: RoleFormData) => ({ role }),
    dataGetter: (response) => response?.data?.updateRole,
  });
  const updateRole = useSubmitMutation(UpdateRole, {
    varSetter: (role: RoleFormData) => ({ role }),
    dataGetter: (response) => response?.data?.updateRole,
    onSave: "/settings/roles",
  });

  const initialFormData = useMemo(() => roleToFormData(role), [role]);

  return (
    <AffinityForm<RoleFormData>
      asyncValidation={validateRole}
      validationSchema={roleDetailsValidationSchema}
      initialData={initialFormData}
      confirmHandler={updateRole}
    >
      <FormView>
        <Layout loading={roleLoading || permissionsLoading} title="Edit role" showBackBtn>
          {!isNil(role) && !isNil(permissionGroups) && <RoleForm permissionGroups={permissionGroups} />}
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title="Confirm and update user role" backLink=".." showBackBtn>
          {!isNil(permissionGroups) && <RoleConfirmation permissionGroups={permissionGroups} />}
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  );
};
